import { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { servicesService, staffService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { PRICE_TYPES } from '../../utils/pricing';

const LOCATION_MODES = [
  { value: 'client_location', label: "Client's location" },
  { value: 'in_person', label: 'In person' },
  { value: 'phone', label: 'Phone call' },
  { value: 'video', label: 'Video call' }
];

const INITIAL = {
  name: '', description: '', duration_minutes: '', price: '', price_type: 'quote', location_mode: 'client_location',
  color: '', buffer_before_minutes: 0, buffer_after_minutes: 0,
  min_notice_minutes: 1440, max_advance_days: 60, is_public: true, is_active: true,
  info_url: '', staff_ids: []
};

export default function ServiceModal({ isOpen, onClose, onSave, service = null }) {
  const { user } = useAuth();
  const isEdit = !!service;
  const [form, setForm] = useState(INITIAL);
  const [staff, setStaff] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    staffService.getAll().then((r) => { if (r.success) setStaff((r.staff || []).filter((p) => p.is_active)); });
    if (service) {
      setForm({
        ...INITIAL,
        ...service,
        price: service.price ?? '',
        duration_minutes: service.duration_minutes ?? '',
        price_type: service.price_type || (service.price !== null && service.price !== undefined ? 'fixed' : 'quote'),
        staff_ids: service.staff_ids || []
      });
    } else {
      // A new job type starts with whoever is creating it on the crew.
      setForm({ ...INITIAL, staff_ids: user?.id ? [user.id] : [] });
    }
  }, [isOpen, service, user?.id]);

  function change(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  }

  function toggleStaff(id) {
    setForm((prev) => ({
      ...prev,
      staff_ids: prev.staff_ids.includes(id) ? prev.staff_ids.filter((x) => x !== id) : [...prev.staff_ids, id]
    }));
    if (errors.staff_ids) setErrors((prev) => ({ ...prev, staff_ids: '' }));
  }

  async function submit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (form.duration_minutes !== '' && Number(form.duration_minutes) < 5) errs.duration_minutes = 'Enter at least 5 minutes, or leave blank for per-job quotes';
    if (!form.staff_ids.length) errs.staff_ids = 'Pick at least one person who does this job';
    if (form.price_type !== 'quote' && form.price !== '' && Number(form.price) < 0) errs.price = 'Price cannot be negative';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    const payload = {
      ...form,
      duration_minutes: form.duration_minutes === '' ? null : Number(form.duration_minutes),
      price: form.price_type === 'quote' || form.price === '' ? null : Number(form.price),
      info_url: (form.info_url || '').trim() || null,
      buffer_before_minutes: Number(form.buffer_before_minutes) || 0,
      buffer_after_minutes: Number(form.buffer_after_minutes) || 0,
      min_notice_minutes: Number(form.min_notice_minutes) || 0,
      max_advance_days: Number(form.max_advance_days) || 60
    };
    delete payload.crew;
    delete payload.crew_names;
    try {
      const response = isEdit
        ? await servicesService.update(service.id, payload)
        : await servicesService.create(payload);
      if (response.success) { onSave?.(response.service); onClose(); }
      else setErrors({ submit: response.error || 'Failed to save job type' });
    } catch (err) {
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  const quoteOnly = form.price_type === 'quote';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Edit Job Type' : 'New Job Type'} size="large">
      <form onSubmit={submit} className="service-form">
        <div className="form-group">
          <label htmlFor="name">Name *</label>
          <input id="name" name="name" value={form.name} onChange={change} placeholder="e.g. Driveway & Concrete Clean" autoFocus />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea id="description" name="description" rows="2" value={form.description} onChange={change} placeholder="What this job covers…" />
        </div>

        <div className="form-group">
          <label>Who does this job *</label>
          <div className="crew-picker">
            {staff.map((p) => (
              <label key={p.id} className={`crew-option ${form.staff_ids.includes(p.id) ? 'on' : ''}`}>
                <input type="checkbox" checked={form.staff_ids.includes(p.id)} onChange={() => toggleStaff(p.id)} />
                <span className="crew-dot" style={{ background: p.color || 'var(--color-teal)' }} />
                {p.name}
              </label>
            ))}
          </div>
          <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 6 }}>
            Everyone ticked is on the job by default, and bookings only show times when all of them are free. You can change the crew on any single appointment.
          </p>
          {errors.staff_ids && <span className="form-error">{errors.staff_ids}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="info_url">Details page URL (optional)</label>
          <input id="info_url" name="info_url" type="url" value={form.info_url || ''} onChange={change}
            placeholder="https://getitdone.getit-media.com.au/… (what's included, before & afters)" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="price_type">Pricing</label>
            <select id="price_type" name="price_type" value={form.price_type} onChange={change}>
              {PRICE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="price">{form.price_type === 'from' ? 'Starting price ($)' : 'Price ($)'}</label>
            <input id="price" name="price" type="number" min="0" step="0.01" value={quoteOnly ? '' : form.price} onChange={change}
              disabled={quoteOnly} placeholder={quoteOnly ? 'Quoted after inspection' : '0 = free'} />
            {errors.price && <span className="form-error">{errors.price}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="location_mode">Location</label>
            <select id="location_mode" name="location_mode" value={form.location_mode} onChange={change}>
              {LOCATION_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="duration_minutes">Duration (minutes, optional)</label>
            <input id="duration_minutes" name="duration_minutes" type="number" min="5" step="5" value={form.duration_minutes} onChange={change}
              placeholder="Blank = per-job quote" title="Leave blank for jobs quoted per job — no time is shown to clients and the calendar sets aside 60 minutes" />
            {errors.duration_minutes && <span className="form-error">{errors.duration_minutes}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="buffer_before_minutes">Buffer before (min)</label>
            <input id="buffer_before_minutes" name="buffer_before_minutes" type="number" min="0" step="5" value={form.buffer_before_minutes} onChange={change} />
          </div>
          <div className="form-group">
            <label htmlFor="buffer_after_minutes">Buffer after (min)</label>
            <input id="buffer_after_minutes" name="buffer_after_minutes" type="number" min="0" step="5" value={form.buffer_after_minutes} onChange={change} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="min_notice_minutes">Min notice (min)</label>
            <input id="min_notice_minutes" name="min_notice_minutes" type="number" min="0" step="30" value={form.min_notice_minutes} onChange={change} />
          </div>
          <div className="form-group">
            <label htmlFor="max_advance_days">Book up to (days ahead)</label>
            <input id="max_advance_days" name="max_advance_days" type="number" min="1" value={form.max_advance_days} onChange={change} />
          </div>
        </div>

        <div className="form-row">
          <label className="checkbox-label">
            <input type="checkbox" name="is_public" checked={!!form.is_public} onChange={change} /> Clients can book this online
          </label>
          <label className="checkbox-label">
            <input type="checkbox" name="is_active" checked={!!form.is_active} onChange={change} /> Active
          </label>
        </div>

        {errors.submit && <div className="form-error text-center mb-md">{errors.submit}</div>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </Modal>
  );
}
